import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isNullUndefinedOrEmpty, SessionType } from '@shared';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@/model';
import { buildManualCcrcTypeAPolicy } from '@/features/ccrc-type-a/lib/ccrc-type-a-pricing';

export interface CcrcTypeAManualFormState {
  productName: string;
  entranceFee: number | null;
  monthlyFee: number | null;
  startYear: number | null;
  annualIncreaseRate: number | null;
}

export type CcrcTypeAManualFormErrors = Partial<Record<keyof CcrcTypeAManualFormState, boolean>>;

type NumericFieldKey = 'entranceFee' | 'monthlyFee' | 'startYear' | 'annualIncreaseRate';

interface UseCcrcTypeAManualFormArgs {
  clientId: string;
  open: boolean;
  onClose: () => void;
  fundingSourceId?: string;
}

function getInitialState(): CcrcTypeAManualFormState {
  return {
    productName: '',
    entranceFee: null,
    monthlyFee: null,
    startYear: new Date().getFullYear(),
    annualIncreaseRate: null,
  };
}

function toFormState(policy: PolicyFundingSource): CcrcTypeAManualFormState {
  return {
    productName: policy.policyCustomName ?? '',
    entranceFee: policy.policyLumpSumPayment ?? null,
    monthlyFee: policy.policyPremiumMonthlyCost ?? null,
    startYear: policy.policyPremiumStartYear ?? new Date().getFullYear(),
    annualIncreaseRate: policy.policyPremiumIncreaseRate ?? null,
  };
}

function validate(state: CcrcTypeAManualFormState): CcrcTypeAManualFormErrors {
  const errors: CcrcTypeAManualFormErrors = {};

  if (!state.productName.trim()) {
    errors.productName = true;
  }

  if (isNullUndefinedOrEmpty(state.entranceFee)) {
    errors.entranceFee = true;
  }

  if (isNullUndefinedOrEmpty(state.monthlyFee)) {
    errors.monthlyFee = true;
  }

  if (isNullUndefinedOrEmpty(state.startYear)) {
    errors.startYear = true;
  }

  return errors;
}

export function useCcrcTypeAManualForm({
  clientId,
  open,
  onClose,
  fundingSourceId,
}: UseCcrcTypeAManualFormArgs) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient)!;
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const [formState, setFormState] = useState<CcrcTypeAManualFormState>(getInitialState);
  const [errors, setErrors] = useState<CcrcTypeAManualFormErrors>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setHasSubmitted(false);

    const existing = fundingSourceId
      ? client.multipleFundingSources.ltcPolicy[fundingSourceId]
      : undefined;

    setFormState(existing ? toFormState(existing) : getInitialState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fundingSourceId]);

  useEffect(() => {
    if (hasSubmitted) {
      setErrors(validate(formState));
    }
  }, [formState, hasSubmitted]);

  const handleNameChange = (value: string) => {
    setFormState(prev => ({ ...prev, productName: value }));
  };

  const handleFieldChange = (field: NumericFieldKey) => (value: number | null) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const validationErrors = validate(formState);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const existing = fundingSourceId
      ? client.multipleFundingSources.ltcPolicy[fundingSourceId]
      : undefined;

    const fundingSourceData: PolicyFundingSource = existing
      ? {
          ...existing,
          policyCustomName: formState.productName.trim(),
          policyLumpSumPayment: formState.entranceFee,
          policyPremiumMonthlyCost: formState.monthlyFee,
          policyPremiumStartYear: formState.startYear,
          policyPremiumIncreaseRate: formState.annualIncreaseRate ?? 0,
        }
      : buildManualCcrcTypeAPolicy({
          productName: formState.productName.trim(),
          entranceFee: formState.entranceFee!,
          monthlyFee: formState.monthlyFee!,
          startYear: formState.startYear!,
          annualIncreaseRate: formState.annualIncreaseRate ?? 0,
          addedByAdvisorId: isAdvisorSession ? advisor?.advisorId : undefined,
        });

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData,
        fundingSourceId: fundingSourceId ?? null,
      }),
    );

    onClose();
  };

  return {
    formState,
    errors,
    handleNameChange,
    handleFieldChange,
    handleSubmit,
  };
}
